<template>
  <section class="relative">
    <MotifBackdrop
      :show-grid="false"
      :show-tiled-pattern="false"
      :show-paper-field="false"
      :random-icons="true"
      :random-density="1"
      :size-min="28"
      :size-max="60"
      :opacity-min="0.10"
      :opacity-max="0.28"
      :kinds="['logo', 'code', 'terminal', 'braces', 'db', 'link']"
      :weights="{ logo: 2, code: 1.5, terminal: 1, mail: 0 }"
      :seed="2025"
    />

    <!-- En-tête -->
    <div class="text-center">
      <div class="max-w-2xl mx-auto text-center relative mb-2">
        <h2
          class="relative z-10 text-4xl sm:text-5xl font-extrabold tracking-tight bg-gradient-to-r from-brand-700 via-indigo-700 to-brand-600 bg-clip-text text-transparent"
        >
          {{ serviceTitle?.titre || (effectiveLang === 'en' ? 'What We Do Best' : 'Ce que nous faisons de mieux') }}
        </h2>
        <p v-if="serviceTitle?.description" class="mt-4 max-w-3xl mx-auto text-base sm:text-lg text-slate-600">
          {{ serviceTitle.description }}
        </p>
      </div>

      <div class="mx-auto mt-6 h-px w-40 bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>

      <!-- Ruban “Populaire” -->
      <!-- <div class="mx-auto mt-2 flex justify-center" v-if="popularOnly">
  <span
    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px]
           text-slate-800 bg-white/60 backdrop-blur-sm ring-1 ring-white/60 shadow-sm
           dark:text-slate-100 dark:bg-white/10 dark:ring-white/20"
  >
    <i class="fa-solid fa-star text-brand-500"></i>
    {{ effectiveLang === 'en' ? 'Popular selection' : 'Sélection populaire' }}
  </span>
</div> -->


      <div class="flex justify-center gap-3 sm:gap-4 mt-6" v-if="isEditMode">
        <button
          @click="editServices"
          class="flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm font-medium
                 text-slate-800 bg-white border border-slate-200 rounded-full hover:bg-slate-50 shadow-sm transition"
        >
          <i class="fas fa-pen-to-square text-sm text-blue-600"></i>
          {{ effectiveLang === 'en' ? 'Edit' : 'Éditer' }}
        </button>
      </div>
    </div>

    <!-- Grille -->
    <div class="mt-10 sm:mt-12 grid gap-6 sm:gap-7 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <!-- Skeletons -->
      <template v-if="loading">
        <div
          v-for="n in 8"
          :key="'skel-' + n"
          class="rounded-3xl overflow-hidden bg-white ring-1 ring-slate-200 shadow-sm"
        >
          <div class="aspect-[16/10] bg-slate-100 animate-pulse"></div>
          <div class="p-5">
            <div class="h-4 w-2/3 bg-slate-100 rounded mb-3 animate-pulse"></div>
            <div class="h-3 w-full bg-slate-100 rounded mb-2 animate-pulse"></div>
            <div class="h-3 w-5/6 bg-slate-100 rounded animate-pulse"></div>
          </div>
        </div>
      </template>

      <!-- État vide -->
      <div
        v-else-if="displayServices.length === 0"
        class="col-span-full text-center px-6 py-12 rounded-2xl border bg-white shadow-sm"
      >
        <div class="inline-grid h-12 w-12 place-items-center rounded-xl bg-slate-100 text-slate-500 mb-3">
          <i class="fa-regular fa-star"></i>
        </div>
        <h3 class="text-lg font-semibold text-slate-900">
          {{ popularOnly
              ? (effectiveLang === 'en' ? 'No popular services yet' : 'Aucun service populaire pour le moment')
              : (effectiveLang === 'en' ? 'No services yet' : 'Aucun service pour le moment') }}
        </h3>
        <p class="mt-1 text-sm text-slate-600" v-if="popularOnly">
          {{ effectiveLang === 'en'
              ? 'Mark services as “Popular” from the admin to feature them here.'
              : 'Marquez des services comme “Populaires” dans l’admin pour les afficher ici.' }}
        </p>
      </div>

      <!-- Cartes -->
      <article
        v-else
        v-for="(srv, index) in displayServices"
        :key="srv?.id || srv?.slug || srv?.titre || srv?.image_url || index"
        class="group relative rounded-3xl overflow-hidden bg-white ring-1 ring-slate-200
               shadow-[0_24px_60px_-30px_rgba(29,78,216,.18)]
               hover:shadow-[0_36px_80px_-30px_rgba(29,78,216,.28)]
               transition-all duration-300 h-full min-h-[360px] flex flex-col"
      >
        <!-- Media -->
        <div class="relative aspect-[16/10] overflow-hidden">
          <img
            :src="srv.image_url || placeholder"
            :alt="imageAlt(srv)"
            class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
            loading="lazy"
          />
          <div class="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

          <div
            v-if="srv.badge"
            class="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-semibold
                   text-white bg-blue-600/90 ring-1 ring-white/10 backdrop-blur-sm"
          >
            {{ srv.badge }}
          </div>

          <!-- ⭐ Popular badge -->
          <div
  v-if="Number(srv.is_popular) === 1"
  class="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
         text-[11px] font-semibold text-slate-900 bg-white/65 backdrop-blur-md ring-1 ring-white/60 shadow-sm
         dark:text-slate-100 dark:bg-white/10 dark:ring-white/20"
  :title="effectiveLang === 'en' ? 'Popular service' : 'Service populaire'"
  aria-label="Popular"
>
  <i class="fa-solid fa-star text-amber-500"></i>
  <span v-if="srv.popular_rank" class="opacity-80">#{{ srv.popular_rank }}</span>
</div>

        </div>

        <!-- Contenu -->
        <div class="relative p-5 flex-1 flex flex-col">
          <!-- Icône (token icon_alt) -->
          <div
            class="absolute -top-7 left-5 h-14 w-14 grid place-items-center rounded-2xl
                   text-white bg-gradient-to-br from-blue-500 to-indigo-600
                   ring-2 ring-white shadow-lg
                   group-hover:shadow-[0_18px_36px_-12px_rgba(29,78,216,.55)] transition"
            :title="iconTitle(srv)"
            aria-hidden="true"
          >
            <i :class="iconClassFromService(srv)" class="text-lg"></i>
          </div>

          <h3 class="mt-2 pt-3 text-lg sm:text-xl font-semibold text-slate-900">
            {{ srv.titre || (effectiveLang === 'en' ? 'Service' : 'Service') }}
          </h3>

          <p class="mt-2 text-[15px] leading-relaxed text-slate-600 line-clamp-4">
            {{ srv.description || (effectiveLang === 'en' ? 'Description' : 'Description du service') }}
          </p>

          <div class="mt-auto pt-4 flex justify-end">
            <router-link
              :to="makeServiceLink(srv)"
              class="inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-semibold
                     text-white bg-blue-600 hover:bg-blue-700 active:scale-[.98]
                     shadow-[0_10px_30px_-12px_rgba(29,78,216,.55)] transition"
            >
              <span>{{ effectiveLang === 'en' ? 'Learn more' : 'En savoir plus' }}</span>
              <i class="fa-solid fa-arrow-right text-[11px]"></i>
            </router-link>
          </div>
        </div>
      </article>
    </div>

    <!-- CTA -->
    <div class="flex justify-center mt-12 sm:mt-14 flex-wrap gap-3 sm:gap-4">
      <router-link
        v-for="(button, idx) in serviceButtons"
        :key="'cta-' + idx"
        :to="button.action || '#'"
        class="relative group inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3 text-sm font-semibold
               rounded-full overflow-hidden transition-all duration-300
               shadow-[0_2px_10px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_18px_rgba(0,0,0,0.08)]
               focus:outline-none focus:ring-2 focus:ring-offset-2"
        :class="[
          (button.style === 'primary')
            ? 'text-white bg-blue-600 focus:ring-blue-300 hover:bg-blue-700'
            : 'text-slate-800 bg-white border border-slate-200 focus:ring-slate-300 hover:bg-slate-50'
        ]"
      >
        <span class="relative z-10">{{ button.label || (effectiveLang === 'en' ? 'Discover' : 'Découvrir') }}</span>
        <i class="fa-solid fa-arrow-right text-xs relative z-10 transition-transform duration-300 group-hover:translate-x-1"></i>
        <span
          class="absolute inset-0 rounded-full bg-black scale-x-0 group-hover:scale-x-100 origin-left
                 transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] transform-gpu pointer-events-none
                 opacity-10"
        ></span>
      </router-link>
    </div>
  </section>
</template>

<script>
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";
import { mapGetters } from "vuex";
import MotifBackdrop from "@/components/ui/MotifBackdrop.vue";

export default {
  name: "ServicesComponent",
  components: { MotifBackdrop },
  props: {
    isEditMode: { type: Boolean, default: false },
    pageId: { type: [Number, String], default: null },
    pageCode: { type: String, default: "home" },
    langue: { type: String, default: null },

    // 🔥 nouveaux contrôles d’affichage
    popularOnly: { type: Boolean, default: false },
    limit: { type: [Number, String], default: null },
  },
  data() {
    return {
      loading: true,
      serviceTitle: null,
      services: [],
      serviceButtons: [],
      _resolvedPageId: null,
      placeholder: "https://placehold.co/1200x675?text=Service",
    };
  },
  computed: {
    ...mapGetters(["getLangue"]),
    effectiveLang() {
      return this.langue || this.getLangue || "fr";
    },
    effectivePageCode() {
      if (this.pageCode) return this.pageCode;
      const n = this.$route?.name || "";
      if (/about/i.test(n)) return "about";
      if (/portfolio/i.test(n)) return "portfolio";
      if (/contact/i.test(n)) return "contact";
      if (/footer/i.test(n)) return "footer";
      return "home";
    },
    primaryCta() {
      const first = (this.serviceButtons || []).find(
        (b) => (b.style || "").toLowerCase() === "primary"
      );
      return first?.action || (this.effectiveLang === "en" ? "/en/contact" : "/fr/contact");
    },
    // ✅ liste affichée (filtrage/tri/limite côté client en fallback)
    displayServices() {
      let arr = Array.isArray(this.services) ? [...this.services] : [];

      if (this.popularOnly) {
        arr = arr.filter(s => Number(s?.is_popular) === 1);
        arr.sort((a, b) => {
          const ra = Number(a?.popular_rank ?? 9999);
          const rb = Number(b?.popular_rank ?? 9999);
          // rang ASC, puis id DESC en cas d'égalité
          return ra - rb || (Number(b?.id || 0) - Number(a?.id || 0));
        });
      }

      if (this.limit && Number(this.limit) > 0) {
        arr = arr.slice(0, Number(this.limit));
      }
      return arr;
    },
  },
  watch: {
    pageId(newVal) {
      if (newVal) {
        this._resolvedPageId = Number(newVal);
        this.fetchByPageId(this._resolvedPageId);
      }
    },
    effectiveLang() {
      if (!this.pageId) this.autoResolveAndFetch();
    },
    "$route.fullPath"() {
      if (!this.pageId) this.autoResolveAndFetch();
    },
    effectivePageCode() {
      if (!this.pageId) this.autoResolveAndFetch();
    },
    isEditMode() {
      if (Number.isFinite(this._resolvedPageId)) {
        this.fetchByPageId(this._resolvedPageId);
      }
    },
    // 🔁 re-fetch si on change le mode populaire/limite (si back-side filtering)
    popularOnly() {
      if (Number.isFinite(this._resolvedPageId)) this.fetchByPageId(this._resolvedPageId);
    },
    limit() {
      if (Number.isFinite(this._resolvedPageId)) this.fetchByPageId(this._resolvedPageId);
    },
  },
  methods: {
    /* ---------- ICONES: helpers ---------- */
    isIconToken(v) {
      return typeof v === "string" && v.trim().toLowerCase().startsWith("icon:");
    },
    iconClassFromToken(token) {
      const fallback = "fa-solid fa-briefcase";
      if (!this.isIconToken(token)) return fallback;
      const raw = token.slice(5).trim();
      const parts = raw.split(/\s+/).filter(Boolean);
      if (parts.length >= 2 && parts[0].startsWith("fa-") && parts[1].startsWith("fa-")) {
        return `${parts[0]} ${parts[1]}`;
      }
      if (parts.length === 1 && parts[0].startsWith("fa-")) {
        return `fa-solid ${parts[0]}`;
      }
      return fallback;
    },
    iconClassFromService(srv = {}) {
      const tok = srv.icon_alt || (this.isIconToken(srv.alt) ? srv.alt : null);
      return this.iconClassFromToken(tok);
    },
    iconTitle(srv = {}) {
      return srv.titre || "Service";
    },
    imageAlt(srv = {}) {
      if (this.isIconToken(srv.alt)) return srv.titre || "Service";
      return srv.alt || srv.titre || "Service";
    },

    /* ---------- Data fetching ---------- */
    async autoResolveAndFetch() {
      try {
        if (this.pageId) {
          this._resolvedPageId = Number(this.pageId);
        } else {
          const lang = this.effectiveLang;
          const code = this.effectivePageCode;
          const idOrObj = await fetchPageIdByCode(code, lang);
          const pid = typeof idOrObj === "number" ? idOrObj : idOrObj?.page_id ?? idOrObj?.id ?? null;
          this._resolvedPageId = Number(pid);
        }
        if (Number.isFinite(this._resolvedPageId)) {
          await this.fetchByPageId(this._resolvedPageId);
        } else {
          console.warn("[ServicesComponent] page_id introuvable", {
            pageCode: this.effectivePageCode,
            lang: this.effectiveLang,
          });
          this.loading = false;
        }
      } catch (e) {
        console.error("[ServicesComponent] autoResolveAndFetch error:", e);
        this.loading = false;
      }
    },
    async fetchByPageId(pid) {
      this.loading = true;
      try {
        // 👇 Inclut les brouillons en mode édition + filtres populaires/limite côté API si dispo
        const params = {
          page_id: pid,
          ...(this.isEditMode ? { includeDraft: 1 } : {}),
          ...(this.popularOnly ? { popularOnly: 1 } : {}),
          ...(this.limit ? { limit: Number(this.limit) } : {}),
        };
        const { data } = await http.get("services", { params });

        this.serviceTitle = data?.serviceTitle || {};

        const raw = Array.isArray(data?.services) ? data.services : [];
        // 👇 Ajoute un badge visuel si brouillon (uniquement en édition)
        this.services = raw.map((s) => ({
          ...s,
          badge:
            this.isEditMode && s.status === "draft"
              ? (this.effectiveLang === "en" ? "Draft" : "Brouillon")
              : s.badge,
        }));

        this.serviceButtons = Array.isArray(data?.boutons) ? data.boutons : [];
      } catch (error) {
        console.error("Erreur Services (page_id):", error);
        this.serviceTitle = {};
        this.services = [];
        this.serviceButtons = [];
      } finally {
        this.loading = false;
      }
    },

    /* ---------- Links ---------- */
    isExternal(href) {
      return /^https?:\/\//i.test(String(href || ""));
    },
    ensureLocalePrefix(path) {
      if (!path) return this.primaryCta || "#";
      if (this.isExternal(path)) return path;
      const lang = this.effectiveLang === "en" ? "en" : "fr";
      if (/^\/(fr|en)\b/.test(path)) return path.replace(/^\/(fr|en)/, `/${lang}`);
      return path.startsWith("/") ? `/${lang}${path}` : `/${lang}/${path}`;
    },
    makeServiceLink(srv) {
      if (srv?.slug) {
        return { name: 'service-detail', params: { langue: this.effectiveLang, slug: srv.slug } };
      }
      if (srv?.action) return this.ensureLocalePrefix(srv.action);
      return this.primaryCta || '#';
    },

    /* ---------- UI ---------- */
    editServices() {
      this.$emit("edit", { serviceTitle: this.serviceTitle, services: this.services });
    },
    addServices() {
      this.$emit("add");
    },
  },
  async mounted() {
    if (this.pageId) {
      this._resolvedPageId = Number(this.pageId);
      await this.fetchByPageId(this._resolvedPageId);
    } else {
      await this.autoResolveAndFetch();
    }
  },
};
</script>

<style scoped>
.title-fade-band::before {
  content: "";
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 50%;
  translate: 0 -50%;
  width: min(95%, 48rem);
  height: 12rem;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, .9),
    rgba(255, 255, 255, .75) 90%,
    rgba(255, 255, 255, 0)
  );
  filter: blur(10px);
  border-radius: 20px;
  z-index: 0;
}
@media (min-width:640px) {
  .title-fade-band::before {
    height: 6rem;
    filter: blur(6px);
  }
}
</style>
